import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { MapPin, Calendar, Clock, Users, Sun, Mountain, ArrowLeft, Instagram } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArticoloSingolo = () => {
  const { slug } = useParams();
  const [travel, setTravel] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTravel = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/travels/${slug}`);
      setTravel(response.data);
    } catch (error) {
      console.error('Error fetching travel:', error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchTravel();
  }, [fetchTravel]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-article">
        <p className="text-foreground/60">Caricamento...</p>
      </div>
    );
  }

  if (!travel) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" data-testid="article-not-found">
        <p className="text-foreground/60 mb-4">Viaggio non trovato</p>
        <Link to="/viaggi" className="text-accent hover:text-accent/80 transition-colors">Torna ai viaggi</Link>
      </div>
    );
  }

  const hasContent = travel.content && travel.content.trim().length > 0;
  const hasLegacyContent = travel.racconto_emozionale || travel.esperienza_familiare || travel.focus_bambini || travel.teaser_pratico;

  return (
    <div data-testid="article-page">
      <div className="relative h-[70vh] md:h-[80vh] overflow-hidden">
        <img
          src={travel.cover_image}
          alt={travel.titolo}
          className="w-full h-full object-cover object-center"
          data-testid="article-cover-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 lg:px-24 pb-12">
          <Link to="/viaggi" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-6" data-testid="back-to-travels-link">
            <ArrowLeft size={20} />Torna ai viaggi
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif text-4xl md:text-6xl text-white max-w-4xl"
            data-testid="article-title"
          >
            {travel.titolo}
          </motion.h1>
        </div>
      </div>

      <article className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 pb-12 border-b border-border" data-testid="article-meta">
            <div>
              <div className="flex items-center gap-2 text-foreground/60 mb-1"><MapPin size={18} /><span className="text-sm font-medium">Destinazione</span></div>
              <p className="font-medium">{travel.destinazione}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-foreground/60 mb-1"><Calendar size={18} /><span className="text-sm font-medium">Quando</span></div>
              <p className="font-medium">{travel.data_viaggio}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-foreground/60 mb-1"><Clock size={18} /><span className="text-sm font-medium">Durata</span></div>
              <p className="font-medium">{travel.durata}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-foreground/60 mb-1"><Users size={18} /><span className="text-sm font-medium">Bambini</span></div>
              <p className="font-medium">{travel.eta_bambini}</p>
            </div>
          </div>

          <div className="space-y-8">
            <section data-testid="article-payoff">
              <p className="font-serif italic text-2xl leading-relaxed text-foreground/90">{travel.payoff}</p>
            </section>

            {hasContent ? (
              <section data-testid="article-content">
                <div
                  className="article-prose"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(travel.content) }}
                />
              </section>
            ) : hasLegacyContent ? (
              <>
                {travel.racconto_emozionale && (
                  <section data-testid="article-emotional-story">
                    <h2 className="font-serif text-3xl mb-4">Il viaggio</h2>
                    <div className="article-prose whitespace-pre-wrap">{travel.racconto_emozionale}</div>
                  </section>
                )}
                {travel.esperienza_familiare && (
                  <section data-testid="article-family-experience">
                    <h2 className="font-serif text-3xl mb-4">L'esperienza in famiglia</h2>
                    <div className="article-prose whitespace-pre-wrap">{travel.esperienza_familiare}</div>
                  </section>
                )}
                {travel.focus_bambini && (
                  <section data-testid="article-children-focus">
                    <h2 className="font-serif text-3xl mb-4">Gli occhi dei bambini</h2>
                    <div className="article-prose whitespace-pre-wrap">{travel.focus_bambini}</div>
                  </section>
                )}
              </>
            ) : null}

            {(travel.difficolta || travel.stagione_consigliata) && (
              <section className="bg-muted rounded-2xl p-8 my-12" data-testid="article-practical-info">
                <div className="flex items-start gap-4 mb-6">
                  <div className="p-3 bg-primary/10 rounded-xl"><Mountain className="text-primary" size={24} /></div>
                  <div>
                    <h3 className="font-serif text-2xl mb-2">Informazioni pratiche</h3>
                    <p className="text-foreground/60">Qualche spunto per organizzare il tuo viaggio</p>
                  </div>
                </div>
                <div className="space-y-6">
                  {travel.stagione_consigliata && (
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2"><Sun size={18} className="text-accent" />Stagione consigliata</h4>
                      <p className="text-foreground/80">{travel.stagione_consigliata}</p>
                    </div>
                  )}
                  {travel.difficolta && (
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2"><Mountain size={18} className="text-accent" />Difficoltà</h4>
                      <p className="text-foreground/80">{travel.difficolta}</p>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </article>

      <section className="py-16 md:py-24 bg-primary text-primary-foreground" data-testid="article-instagram-cta">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-serif text-3xl mb-4">Seguici su Instagram</h3>
          <p className="text-primary-foreground/80 leading-relaxed mb-8">
            Unisciti alla community per vedere foto e video dietro le quinte dei nostri viaggi.
          </p>
          <a
            href="https://instagram.com/chichi_inviaggio"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-lg font-medium bg-white text-primary hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            data-testid="article-instagram-button"
          >
            <Instagram size={20} />@chichi_inviaggio
          </a>
        </div>
      </section>
    </div>
  );
};

export default ArticoloSingolo;
